<div class="config-page">
  <div class="loading-container" *ngIf="loading || saving">
    <div class="loading-panel">
      <mat-spinner diameter="34"></mat-spinner>
      <span>{{
        saving ? 'Saving changes...' : 'Loading configurations...'
      }}</span>
    </div>
  </div>

  <header class="config-header">
    <div>
      <a mat-button routerLink="/schedule" class="back-link">
        <mat-icon>arrow_back</mat-icon>
        Schedule
      </a>
      <h1>DC Roster Configurations</h1>
      <p>
        {{ filteredConfigCount }} configurations · {{ filteredCodeCount }} codes
      </p>
    </div>

    <button
      mat-raised-button
      color="primary"
      type="button"
      (click)="openAddConfig()"
      [disabled]="!hasWritePermission || loading || saving"
    >
      <mat-icon>add</mat-icon>
      Add Config
    </button>
  </header>

  <div class="config-toolbar">
    <div class="schedule-filter-group search-filter">
      <label for="config-search-filter">Search</label>
      <input
        id="config-search-filter"
        class="filter-control"
        type="search"
        placeholder="Type, subtype, description, or code"
        [ngModel]="searchInputValue"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onSearchChanged($event)"
      />
    </div>

    <div class="schedule-filter-group">
      <label for="config-dc-type-filter">DC Type</label>
      <select
        id="config-dc-type-filter"
        class="filter-control"
        [(ngModel)]="dcTypeFilter"
        [ngModelOptions]="{ standalone: true }"
      >
        <option value="">All</option>
        <option *ngFor="let dcType of dcTypeOptions" [value]="dcType">
          {{ dcType }}
        </option>
      </select>
    </div>

    <div class="schedule-filter-group">
      <label for="config-subtype-filter">SubType</label>
      <select
        id="config-subtype-filter"
        class="filter-control"
        [(ngModel)]="subTypeFilter"
        [ngModelOptions]="{ standalone: true }"
      >
        <option value="">All</option>
        <option *ngFor="let subType of subTypeOptions" [value]="subType">
          {{ subType }}
        </option>
      </select>
    </div>

    <div class="schedule-filter-group">
      <label for="config-collection-type-filter">Collection Type</label>
      <select
        id="config-collection-type-filter"
        class="filter-control"
        [(ngModel)]="collectionTypeFilter"
        [ngModelOptions]="{ standalone: true }"
      >
        <option value="">All</option>
        <option
          *ngFor="let collectionType of collectionTypes"
          [value]="collectionType"
        >
          {{ collectionType }}
        </option>
      </select>
    </div>

    <button
      mat-button
      type="button"
      class="clear-button"
      (click)="clearFilters()"
    >
      <mat-icon>close</mat-icon>
      Clear
    </button>
  </div>

  <table
    mat-table
    [dataSource]="filteredConfigs"
    [trackBy]="trackByConfigId"
    multiTemplateDataRows
    class="config-table mat-elevation-z2"
  >
    <ng-container matColumnDef="expand">
      <th mat-header-cell *matHeaderCellDef class="expand-column"></th>
      <td mat-cell *matCellDef="let config" class="expand-column">
        <button
          mat-icon-button
          type="button"
          class="expand-toggle"
          (click)="$event.stopPropagation(); toggleExpanded(config)"
          [attr.aria-label]="
            isExpanded(config)
              ? 'Collapse configuration'
              : 'Expand configuration'
          "
        >
          <mat-icon>{{
            isExpanded(config) ? 'keyboard_arrow_down' : 'chevron_right'
          }}</mat-icon>
        </button>
      </td>
    </ng-container>

    <ng-container matColumnDef="dcType">
      <th mat-header-cell *matHeaderCellDef>DC Type</th>
      <td mat-cell *matCellDef="let config">
        <span class="dc-type-badge">{{ config.dcType || '—' }}</span>
      </td>
    </ng-container>

    <ng-container matColumnDef="description">
      <th mat-header-cell *matHeaderCellDef>Description</th>
      <td mat-cell *matCellDef="let config">
        {{ config.description || '—' }}
      </td>
    </ng-container>

    <ng-container matColumnDef="subType">
      <th mat-header-cell *matHeaderCellDef>SubType</th>
      <td mat-cell *matCellDef="let config">{{ config.subType || '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="codes">
      <th mat-header-cell *matHeaderCellDef>Codes</th>
      <td mat-cell *matCellDef="let config">
        {{ config.collectionEvents.length }}
      </td>
    </ng-container>

    <ng-container matColumnDef="actions">
      <th mat-header-cell *matHeaderCellDef class="actions-column">Actions</th>
      <td
        mat-cell
        *matCellDef="let config"
        class="actions-column"
        (click)="$event.stopPropagation()"
      >
        <button
          mat-icon-button
          type="button"
          aria-label="Edit configuration"
          (click)="openEditConfig(config)"
          [disabled]="!hasWritePermission || saving"
        >
          <mat-icon>edit</mat-icon>
        </button>
        <button
          mat-icon-button
          color="warn"
          type="button"
          aria-label="Delete configuration"
          (click)="confirmDeleteConfig(config)"
          [disabled]="!hasWritePermission || saving"
        >
          <mat-icon>delete_outline</mat-icon>
        </button>
      </td>
    </ng-container>

    <ng-container matColumnDef="expandedDetail">
      <td
        mat-cell
        *matCellDef="let config"
        [attr.colspan]="displayedColumns.length"
        class="detail-cell"
      >
        <section class="event-detail" *ngIf="isExpanded(config)">
          <div class="event-detail-header">
            <h2>Collection Codes</h2>
            <button
              mat-stroked-button
              type="button"
              (click)="openAddEvent(config)"
              [disabled]="!hasWritePermission || saving"
            >
              <mat-icon>add</mat-icon>
              Add Code
            </button>
          </div>

          <table
            mat-table
            [dataSource]="config.collectionEvents"
            [trackBy]="trackByEventCode"
            class="event-table"
          >
            <ng-container matColumnDef="code">
              <th mat-header-cell *matHeaderCellDef>Code</th>
              <td mat-cell *matCellDef="let event">
                <code>{{ event.code || '—' }}</code>
              </td>
            </ng-container>

            <ng-container matColumnDef="collectionType">
              <th mat-header-cell *matHeaderCellDef>Collection Type</th>
              <td mat-cell *matCellDef="let event">
                {{ event.collectionType || '—' }}
              </td>
            </ng-container>

            <ng-container matColumnDef="effort">
              <th mat-header-cell *matHeaderCellDef>Effort</th>
              <td mat-cell *matCellDef="let event">
                {{ event.effort ?? '—' }}
              </td>
            </ng-container>

            <ng-container matColumnDef="eventGrade">
              <th mat-header-cell *matHeaderCellDef>Event Grade</th>
              <td mat-cell *matCellDef="let event">
                {{ event.eventGrade ?? '—' }}
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef class="actions-column">
                Actions
              </th>
              <td mat-cell *matCellDef="let event" class="actions-column">
                <button
                  mat-icon-button
                  type="button"
                  aria-label="Edit collection code"
                  (click)="openEditEvent(config, event)"
                  [disabled]="!hasWritePermission || saving"
                >
                  <mat-icon>edit</mat-icon>
                </button>
                <button
                  mat-icon-button
                  color="warn"
                  type="button"
                  aria-label="Delete collection code"
                  (click)="confirmDeleteEvent(config, event)"
                  [disabled]="!hasWritePermission || saving || event.reserved"
                  [matTooltip]="
                    event.reserved ? 'Reserved code, cannot be deleted' : ''
                  "
                >
                  <mat-icon>delete_outline</mat-icon>
                </button>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="eventDisplayedColumns"></tr>
            <tr
              mat-row
              *matRowDef="let event; columns: eventDisplayedColumns"
            ></tr>

            <tr class="mat-row" *matNoDataRow>
              <td
                class="mat-cell empty-row"
                [attr.colspan]="eventDisplayedColumns.length"
              >
                No collection codes in this configuration.
              </td>
            </tr>
          </table>
        </section>
      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
    <tr
      mat-row
      *matRowDef="let config; columns: displayedColumns"
      class="config-row"
      [class.expanded]="isExpanded(config)"
      (click)="toggleExpanded(config)"
    ></tr>
    <tr
      mat-row
      *matRowDef="let config; columns: detailRowColumns"
      class="detail-row"
      [class.hidden-detail-row]="!isExpanded(config)"
    ></tr>

    <tr class="mat-row" *matNoDataRow>
      <td class="mat-cell empty-row" [attr.colspan]="displayedColumns.length">
        No configurations match the active filters.
      </td>
    </tr>
  </table>
</div>
